import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { createURN } from 'garaje/models/generic-resource';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type {
  ResourceCheckInStrategyType,
  ResourceInviteSignInActionStrategyType,
  ResourceMissedCheckInCleanupStrategyType,
  ResourceMissedCheckInStrategyType,
  ResourceTypeConfig,
} from 'garaje/types/generic-resources/ResourceTypeConfig';
import { hourOptions } from 'garaje/utils/hour-options';
import urlBuilder from 'garaje/utils/url-builder';
import _camelCase from 'lodash/camelCase';

interface CancellationStrategyPanelArgs {
  resourceTypeId: number;
  resourceTypeConfig: ResourceTypeConfig;
}

interface CancellationStrategy {
  checkInStrategy: ResourceCheckInStrategyType;
  missedCheckInStrategy: ResourceMissedCheckInStrategyType;
  missedCheckInCleanupStrategy: ResourceMissedCheckInCleanupStrategyType;
  inviteSignInActionStrategy: ResourceInviteSignInActionStrategyType;
}

const DEFAULT_HOUR_OPTION = { value: '10:00', label: '10:00 am' };
const DEFAULT_CHECK_IN_STRATEGY = {
  key: 'CheckInStrategy',
  type: 'DayOfCheckInStrategy',
  properties: {},
};

const DEFAULT_MISSED_CHECK_IN_STRATEGY = {
  key: 'MissedCheckInStrategy',
  type: 'SetTimeOfDayMissedCheckInStrategy',
  properties: {
    checkInBy: DEFAULT_HOUR_OPTION.value,
  },
};

const DEFAULT_MISSED_CHECK_IN_CLEANUP_STRATEGY = {
  key: 'MissedCheckInCleanupStrategy',
  type: 'EnabledMissedCheckInCleanupStrategy',
  properties: {},
};

const DEFAULT_INVITE_SIGN_IN_ACTION_STRATEGY = {
  key: 'InviteSignInActionStrategy',
  type: 'FirstReservationOnlyInviteSignInActionStrategy',
  properties: {},
};

const DISABLED_CHECK_IN_STRATEGY = {
  key: 'CheckInStrategy',
  type: 'DisabledCheckInStrategy',
  properties: {},
};

const DISABLED_MISSED_CHECK_IN_STRATEGY = {
  key: 'MissedCheckInStrategy',
  type: 'DisabledMissedCheckInStrategy',
  properties: {},
};

const DISABLED_MISSED_CHECK_IN_CLEANUP_STRATEGY = {
  key: 'MissedCheckInCleanupStrategy',
  type: 'DisabledMissedCheckInCleanupStrategy',
  properties: {},
};

const DISABLED_INVITE_SIGN_IN_ACTION_STRATEGY = {
  key: 'InviteSignInActionStrategy',
  type: 'DisabledInviteSignInActionStrategy',
  properties: {},
};

const DEFAULT_CANCELLATION_STRATEGIES = [
  DEFAULT_CHECK_IN_STRATEGY,
  DEFAULT_MISSED_CHECK_IN_STRATEGY,
  DEFAULT_MISSED_CHECK_IN_CLEANUP_STRATEGY,
  DEFAULT_INVITE_SIGN_IN_ACTION_STRATEGY,
];

const DISABLED_CANCELLATION_STRATEGIES = [
  DISABLED_CHECK_IN_STRATEGY,
  DISABLED_MISSED_CHECK_IN_STRATEGY,
  DISABLED_MISSED_CHECK_IN_CLEANUP_STRATEGY,
  DISABLED_INVITE_SIGN_IN_ACTION_STRATEGY,
];

const CANCELLATION_STRATEGY_KEYS = [
  'CheckInStrategy',
  'MissedCheckInStrategy',
  'MissedCheckInCleanupStrategy',
  'InviteSignInActionStrategy',
];

const DISABLED_CANCELLATION_STRATEGY_MAP: Record<string, string> = {
  checkInStrategy: 'DisabledCheckInStrategy',
  missedCheckInStrategy: 'DisabledMissedCheckInStrategy',
  missedCheckInCleanupStrategy: 'DisabledMissedCheckInCleanupStrategy',
  inviteSignInActionStrategy: 'DisabledInviteSignInActionStrategy',
};

export default class CancellationStrategyPanel extends Component<CancellationStrategyPanelArgs> {
  @service declare ajax: AjaxFetchService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;

  @tracked isToggled: boolean = false;
  @tracked selectedCheckInDeadline = DEFAULT_HOUR_OPTION.value;

  constructor(owner: unknown, args: CancellationStrategyPanelArgs) {
    super(owner, args);

    this.isToggled = !this.isCurrentStrategyDefault;
    this.selectedCheckInDeadline =
      (this.currentCancellationStrategy.missedCheckInStrategy.properties?.checkInBy as string) ||
      DEFAULT_HOUR_OPTION.value;
  }

  get defaultHourOption(): { value: string; label: string } {
    return DEFAULT_HOUR_OPTION;
  }

  get isCurrentStrategyDefault(): boolean {
    return Object.entries(this.currentCancellationStrategy).every(
      ([key, strategy]: [string, CancellationStrategy[keyof CancellationStrategy]]) => {
        return DISABLED_CANCELLATION_STRATEGY_MAP[key] === (strategy.type as string);
      },
    );
  }

  get currentCancellationStrategy(): CancellationStrategy {
    const { settings } = this.args.resourceTypeConfig;

    return CANCELLATION_STRATEGY_KEYS.reduce((acc, key) => {
      const strategy = settings.find((setting) => setting.key === key);
      return {
        ...acc,
        [_camelCase(key)]: strategy,
      };
    }, {} as CancellationStrategy);
  }

  get deadlineHourOptions(): { value: string; label: string }[] {
    return hourOptions(30, false, 'HH:mm');
  }

  getNonCancellationSettings(): ResourceTypeConfig['settings'] {
    const { settings } = this.args.resourceTypeConfig;
    return settings.filter(
      (setting) => !CANCELLATION_STRATEGY_KEYS.includes(setting.key as (typeof CANCELLATION_STRATEGY_KEYS)[number]),
    );
  }

  setDisabledCancellationStrategy = (): ResourceTypeConfig['settings'] => {
    return [
      ...this.getNonCancellationSettings(),
      ...DISABLED_CANCELLATION_STRATEGIES,
    ] as ResourceTypeConfig['settings'];
  };

  setDefaultCancellationStrategy = (): ResourceTypeConfig['settings'] => {
    return [...this.getNonCancellationSettings(), ...DEFAULT_CANCELLATION_STRATEGIES] as ResourceTypeConfig['settings'];
  };

  @action
  async handleToggleCancellationStrategy(): Promise<void> {
    const updatedCancellationStrategy = this.isToggled
      ? this.setDisabledCancellationStrategy()
      : this.setDefaultCancellationStrategy();

    try {
      await this.updateCancellationStrategy.perform(updatedCancellationStrategy);
      this.isToggled = !this.isToggled;
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Could not update cancellation policy');
    }
  }

  @action
  async handleSelectCheckInDeadline(option: { value: string; label: string }): Promise<void> {
    if (!this.isToggled) return;
    this.selectedCheckInDeadline = option.value;

    const updatedMissedCheckInStrategy = this.setDefaultCancellationStrategy();
    const missedCheckInStrategy = updatedMissedCheckInStrategy.find(
      (strategy) => strategy.key === 'MissedCheckInStrategy',
    );
    if (missedCheckInStrategy && 'properties' in missedCheckInStrategy) {
      missedCheckInStrategy.properties.checkInBy = option.value;
    }

    try {
      await this.updateCancellationStrategy.perform(updatedMissedCheckInStrategy);
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Could not update check-in deadline');
    }
  }

  updateCancellationStrategy = task({ drop: true }, async (updatedConfigSettings: ResourceTypeConfig['settings']) => {
    const url = urlBuilder.rms.resourceTypes();

    await this.ajax.request(url, {
      method: 'PUT',
      contentType: 'application/json',
      data: JSON.stringify({
        data: {
          ...this.args.resourceTypeConfig,
          relationId: createURN('location', this.state.currentLocation.id),
          settings: updatedConfigSettings,
        },
      }),
    });
  });
}
